/**
 * MyManagerObjectives — Carte spécifique Cyrille (Manager).
 *
 * Affiche ses objectifs commerciaux manager du mois courant (mois sélectionnable) :
 *   - CA personnel (objectif vs réalisé + taux d'atteinte)
 *   - Nombre de ventes (objectif vs réalisé + taux d'atteinte)
 *   - Rappel du barème commercial manager (CA 60% · Nb ventes 25% · Conversion 15%)
 *
 * Cette logique est DISTINCTE des modules Sales / SkinCare standards. Source
 * de vérité : `scoring_monthly_entries.cyrille_commercial` (admin saisie).
 *
 * Visible uniquement si l'utilisateur courant est manager (Cyrille).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Crown, TrendingUp, Target, Wallet } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtFcfa = (n) => {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';
};
const fmtInt = (n) => (n == null || isNaN(n) ? '—' : new Intl.NumberFormat('fr-FR').format(Math.round(n)));

const RateBadge = ({ rate }) => {
  if (rate == null || isNaN(rate)) {
    return <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-500 font-semibold">Non défini</span>;
  }
  const cls = rate >= 100 ? 'bg-emerald-100 text-emerald-700'
    : rate >= 75 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cls}`}>{rate.toFixed(0)}%</span>;
};

const ProgressBar = ({ rate, color = '#D97706' }) => {
  const pct = Math.min(100, Math.max(0, rate || 0));
  return (
    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

const MyManagerObjectives = () => {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/agent-scoring/score/me?period=${period}`, { headers: auth() });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { reload(); }, [reload]);

  // Garde : si l'utilisateur n'est pas Cyrille (data absent OU is_cyrille=false),
  // on n'affiche rien du tout. Pendant le chargement initial, on ne montre rien
  // non plus pour éviter un flash de carte vide à d'autres profils.
  if (loading) return null;
  if (!data || !data.is_cyrille) return null;

  const com = data?.cyrille_commercial || {};
  const caObj = com.ca_objectif;
  const caReal = com.ca_realise;
  const caRate = caObj > 0 ? (caReal || 0) / caObj * 100 : null;

  const nbObj = com.nb_ventes_objectif;
  const nbReal = com.nb_ventes_realise;
  const nbRate = nbObj > 0 ? (nbReal || 0) / nbObj * 100 : null;

  return (
    <Card className="border-2 border-amber-200 shadow-card rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50/60 to-white" data-testid="my-manager-objectives-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-600" />
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-900">Mes objectifs commerciaux · Manager</h3>
              <p className="text-xs text-zinc-500">
                Période <strong>{period}</strong> · barème spécifique manager (distinct Sales/SkinCare)
              </p>
            </div>
          </div>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9 px-2 rounded border border-zinc-300 text-sm"
            data-testid="manager-objectives-period"
          />
        </div>

        {(com.ca_objectif == null && com.nb_ventes_objectif == null) ? (
          <div className="text-center py-6 border-2 border-dashed border-amber-200 rounded-lg">
            <Target className="w-8 h-8 mx-auto text-amber-300 mb-2" />
            <p className="text-sm text-zinc-500">Aucun objectif commercial saisi pour {period}.</p>
            <p className="text-xs text-zinc-400 mt-1">L&apos;administration définit vos objectifs mensuels depuis l&apos;écran Scoring &amp; Barèmes.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* CA personnel */}
            <div className="p-4 rounded-xl bg-white border border-amber-200" data-testid="manager-obj-ca">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-amber-600" />
                  <h4 className="text-sm font-bold text-zinc-900">Chiffre d&apos;affaires personnel</h4>
                </div>
                <RateBadge rate={caRate} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Réalisé</p>
                  <p className="text-xl font-bold text-zinc-900">{fmtFcfa(caReal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Objectif</p>
                  <p className="text-xl font-semibold text-zinc-600">{fmtFcfa(caObj)}</p>
                </div>
              </div>
              <div className="mt-3"><ProgressBar rate={caRate} /></div>
            </div>

            {/* Nb ventes */}
            <div className="p-4 rounded-xl bg-white border border-amber-200" data-testid="manager-obj-nbventes">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                  <h4 className="text-sm font-bold text-zinc-900">Nombre de ventes</h4>
                </div>
                <RateBadge rate={nbRate} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Réalisé</p>
                  <p className="text-xl font-bold text-zinc-900">{fmtInt(nbReal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Objectif</p>
                  <p className="text-xl font-semibold text-zinc-600">{fmtInt(nbObj)}</p>
                </div>
              </div>
              <div className="mt-3"><ProgressBar rate={nbRate} /></div>
            </div>

            {/* Barème commercial manager */}
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="manager-obj-bareme">
              <p className="font-bold mb-1">Barème commercial manager</p>
              <p>
                Ce bloc compte pour l&apos;axe « Performance commerciale perso » de votre évaluation manager.
                Répartition interne&nbsp;: <strong>CA 60%</strong> · <strong>Nb ventes 25%</strong> · <strong>Qualité conversion 15%</strong>.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyManagerObjectives;
